import { Fragment } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ChevronRight, Home, Briefcase, Users, DollarSign, Brain, Settings, User, Building, FileText, Plane, Ticket, Building2 } from 'lucide-react';
import { useBreadcrumbStore, BreadcrumbEntityType, BreadcrumbDomain } from '../../stores/breadcrumbStore';
import { cn } from '@/lib/cn';

// Comprehensive route name mappings
const routeNameMap: Record<string, string> = {
  // Dashboard
  'dashboard': 'Dashboard',
  'locations': 'Customer Locations',
  'aoc': 'Aviation Ops Center',
  'ops': 'Operations Command',
  'inbox': 'Ops Inbox',
  'dispatch': 'Team Dispatch',
  'control-tower': 'Ops Control Tower',
  'handbook': 'AOC + Ops Handbook',

  // Bookings & Search
  'search': 'Flight Search',
  'advanced': 'Advanced Search',
  'consolidators': 'Agentic Fare Search',
  'quick-book': 'Quick Book',
  'price-comparison': 'Price Compare',
  'bookings': 'All Bookings',
  'groups': 'Group Bookings',

  // PNR & Tickets
  'pnr': 'PNR Manager',
  'pnt': 'PNT Tracker',
  'payment-economics': 'Payment Economics',
  'history': 'History',
  'record': 'PNR Record',

  // Trips
  'trips': 'All Trips',
  'trip-files': 'Trip Files',
  'itinerary': 'Itinerary',
  'builder': 'Builder',
  'proposals': 'Proposals',
  'tracking': 'Tracking',

  // Hotels & Cars
  'hotels': 'Hotels',
  'ratehawk': 'RateHawk',
  'cars': 'Car Rental',

  // GDS
  'sabre': 'Sabre',
  'terminal': 'Terminal',
  'availability': 'Availability',
  'pnr-create': 'PNR Create',
  'fare-rules': 'Fare Rules',
  'ancillary': 'Ancillary Services',
  'duffel': 'Duffel NDC',
  'portal-profiles': 'Portal Browser Profiles',

  // Queues & Automation
  'automation': 'Automation',
  'queues': 'Queue Dashboard',
  'operations': 'Operations',
  'queue': 'Smart Queue',
  'queue-actions': 'Queue Actions',
  'ticketing': 'Ticketing Queue',
  'schedule-changes': 'Schedule Changes',
  'ttl': 'Time Limits',
  'queue-rules': 'Queue Automation',
  'checkin': 'Auto Check-In',

  // Customer Hub
  'customers': 'Customers',
  'crm': 'CRM',
  'segments': 'Segments',
  'companies': 'Companies',
  'travelers': 'Travelers',
  'leads': 'Leads',
  'suppliers': 'Suppliers',
  'rates': 'Rates',
  'communications': 'Unified Comms',
  'communication-hub': 'Communication Hub',
  'communication-logs': 'Communication Logs',
  'documents': 'Documents',
  'vip-support': 'VIP Support',
  'vip': 'VIP',
  'person': 'Person',
  'generate': 'Generate',
  'manager': 'Manager',

  // Planning Tools
  'rtw': 'RTW Planner',
  'loyalty': 'Loyalty Calculator',
  'calc': 'Quick Points Calc',
  'tier-points-calculator': 'Tier Points Calculator',

  // Tasks
  'tasks': 'Tasks',
  'management': 'Management',
  'action-plans': 'Action Plans',
  'routines': 'Routines',
  'qc-checklists': 'QC Checklists',
  'acknowledgements': 'Acknowledgements',

  // AI Tools
  'ai-assistant': 'AI Assistant',
  'ai': 'AI',
  'sabre-agent': 'Sabre AI Agent',
  'threads': 'AI Threads',
  'pnr-agent': 'AI PNR Agent',
  'pnr-parser': 'PNR Parser',
  'knowledge-base': 'Knowledge Base',
  'stats': 'AI Usage Stats',
  'api-docs': 'API Reference',

  // Reports & Analytics
  'reports': 'Reports',
  'revenue': 'Revenue',
  'analytics': 'Analytics',
  'agents': 'Agent Performance',
  'agent-performance': 'Agent Metrics & Targets',

  // Risk & Alerts
  'risk': 'Travel Risk',
  'alerts': 'Flight Alerts',
  'flights': 'Flight Alerts',
  'smart-alerts': 'Smart Alerts',

  // Support
  'freshdesk': 'Freshdesk',
  'sync': 'Sync Status',
  'data-quality': 'Data Quality',

  // Finance
  'finance': 'Finance',
  'invoices': 'Invoices',
  'templates': 'Templates',
  'service-fees': 'Service Fees',
  'payments': 'Payments',
  'refunds': 'Refunds',
  'payment-plans': 'Payment Plans',
  'reconciliation': 'Reconciliation',
  'banking': 'Banking',
  'categorize': 'Categorize',
  'rules': 'Rules',
  'categories': 'Categories',
  'stripe-balance': 'Stripe Balance',
  'stripe-settlement': 'Stripe Settlement',
  'stripe-customers': 'Stripe Customers',
  'chart-of-accounts': 'Chart of Accounts',
  'journal-entries': 'Journal Entries',
  'ar-aging': 'AR Aging',
  'ap-management': 'AP Management',
  'cashflow': 'Cash Flow',
  'aging': 'PNT Aging',
  'supplier-invoices': 'Supplier Invoices',
  'supplier-credit': 'Supplier Credit',
  'supplier-config': 'Supplier Setup',
  'statement-reconciliation': 'Statement Reconciliation',
  'settlements': 'Settlements',
  'commissions': 'Commissions',
  'commission-engine': 'Commission Engine',

  // Partners
  'partners': 'Partners',
  'payouts': 'Payouts',
  'new': 'New',

  // Admin
  'admin': 'Admin',
  'users': 'Users',
  'permissions': 'Permissions',
  'rbac': 'RBAC',
  'approvals': 'Approvals',
  'email-templates': 'Email Templates',
  'audit-log': 'Audit Log',

  // Integrations
  'integrations': 'Integrations',
  'gds': 'GDS Configuration',
  'todoist': 'Todoist',
  'pipedream': 'Pipedream',

  // Loyalty
  'loyalty-programs': 'Loyalty Programs',
  'earning-rules': 'Earning Rules',
  'earning-charts': 'Earning Charts',
  'calculation-tester': 'Calculation Tester',

  // System
  'settings': 'Settings',
  'login-cms': 'Login CMS',
  'web-scraper': 'Web Scraper',

  // Public Portal
  'propeller': 'Propeller',
  'award-search': 'Award Search',
  'seat-maps': 'Seat Maps',
  'flifo': 'Flight Info',

  // Expenses
  'expenses': 'Expenses',
  'compliance': 'Compliance',
};

// Domain icons
const domainIcons: Record<BreadcrumbDomain, typeof Briefcase> = {
  operations: Briefcase,
  crm: Users,
  finance: DollarSign,
  intelligence: Brain,
  admin: Settings,
};

// Domain display names
const domainNames: Record<BreadcrumbDomain, string> = {
  operations: 'Operations',
  crm: 'CRM',
  finance: 'Finance',
  intelligence: 'Intelligence',
  admin: 'Admin',
};

// Entity type icons
const entityIcons: Record<BreadcrumbEntityType, typeof User> = {
  customer: User,
  company: Building,
  traveler: User,
  person: User,
  pnr: FileText,
  invoice: FileText,
  trip: Plane,
  booking: Ticket,
  supplier: Building2,
  partner: Users,
};

// Map paths to domains
const pathToDomain: Record<string, BreadcrumbDomain> = {
  // Operations domain (day-to-day TMC workflows)
  'dashboard': 'operations',
  'search': 'operations',
  'bookings': 'operations',
  'pnr': 'operations',
  'pnt': 'operations',
  'trips': 'operations',
  'trip-files': 'operations',
  'itinerary': 'operations',
  'proposals': 'operations',
  'hotels': 'operations',
  'cars': 'operations',
  'sabre': 'operations',
  'duffel': 'operations',
  'automation': 'operations',
  'operations': 'operations',
  'rtw': 'operations',
  'loyalty': 'operations',
  'tasks': 'operations',
  'action-plans': 'operations',
  'routines': 'operations',
  'groups': 'operations',
  'quick-book': 'operations',
  'price-comparison': 'operations',
  'consolidators': 'operations',
  'aoc': 'operations',
  'ops': 'operations',

  // CRM domain (customer relationship management)
  'customers': 'crm',
  'companies': 'crm',
  'travelers': 'crm',
  'leads': 'crm',
  'crm': 'crm',
  'communications': 'crm',
  'communication-logs': 'crm',
  'communication-hub': 'crm',
  'documents': 'crm',
  'vip-support': 'crm',
  'suppliers': 'crm',

  // Finance domain
  'finance': 'finance',
  'invoices': 'finance',
  'payments': 'finance',
  'banking': 'finance',
  'commissions': 'finance',
  'partners': 'finance',
  'expenses': 'finance',

  // Intelligence domain (AI, analytics, reports, risk)
  'ai-assistant': 'intelligence',
  'ai': 'intelligence',
  'reports': 'intelligence',
  'revenue': 'intelligence',
  'analytics': 'intelligence',
  'risk': 'intelligence',
  'alerts': 'intelligence',
  'smart-alerts': 'intelligence',
  'freshdesk': 'intelligence',
  'data-quality': 'intelligence',
  'knowledge-base': 'intelligence',

  // Admin domain
  'api-docs': 'admin',
  'admin': 'admin',
  'settings': 'admin',
  'integrations': 'admin',
  'automations': 'admin',
  'templates': 'admin',
  'propeller': 'admin',
  'compliance': 'admin',
  'qc-checklists': 'crm',
};

// Redirect prefix-only parent segments that have no standalone route in router.tsx
const PREFIX_PARENT_REDIRECTS: Record<string, string> = {
  '/ops': '/ops/today',
  '/itinerary': '/itinerary/builder',
  '/ai': '/ai-assistant',
  '/analytics': '/reports',
  '/automations': '/admin/automations',
};

function resolveParentHref(href: string): string {
  return PREFIX_PARENT_REDIRECTS[href] || href;
}

export function Breadcrumbs() {
  const location = useLocation();
  const { overrides, entityContext, currentDomain, currentSection } = useBreadcrumbStore();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Determine the domain from path unless explicit context was set.
  const firstSegment = pathnames[0] || 'dashboard';
  const domain = currentDomain || pathToDomain[firstSegment] || 'operations';
  const DomainIcon = domainIcons[domain];

  const domainRoute: Record<BreadcrumbDomain, string> = {
    operations: '/dashboard',
    crm: '/crm',
    finance: '/finance',
    intelligence: '/reports',
    admin: '/admin',
  };

  if (pathnames.length === 0) {
    const OpsIcon = domainIcons.operations;
    return (
      <nav className="flex items-center text-sm text-gray-500 mb-4 flex-wrap gap-y-1" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-blue-600 flex items-center transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4 mx-1.5 text-gray-400" />
        <Link to="/dashboard" className="flex items-center gap-1.5 text-gray-600 hover:text-blue-600 transition-colors">
          <OpsIcon className="w-3.5 h-3.5" />
          <span className="font-medium">Operations</span>
        </Link>
        <ChevronRight className="w-4 h-4 mx-1.5 text-gray-400" />
        <Link to="/" aria-current="page" className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
          Dashboard
        </Link>
      </nav>
    );
  }

  const fallbackSectionFromPath = (() => {
    if (pathnames.length === 0) return 'Dashboard';
    if (pathnames[0] === 'crm' && pathnames[1]) {
      return routeNameMap[pathnames[1]] || pathnames[1];
    }
    return routeNameMap[pathnames[0]] || pathnames[0];
  })();

  const sectionLabel = currentSection
    ? (routeNameMap[currentSection] || currentSection.charAt(0).toUpperCase() + currentSection.slice(1).replace(/-/g, ' '))
    : fallbackSectionFromPath.charAt(0).toUpperCase() + fallbackSectionFromPath.slice(1).replace(/-/g, ' ');
  const rawSectionHref = pathnames[0] === 'crm' && pathnames[1]
    ? `/crm/${pathnames[1]}`
    : `/${pathnames[0] || ''}`;
  const sectionHref = resolveParentHref(rawSectionHref);

  // Avoid duplicate "Finance > Finance", "Admin > Admin > Users", or "Operations > Operations > Today"
  const isDuplicateDomainSection =
    sectionLabel.toLowerCase() === domainNames[domain].toLowerCase();
  const domainHref = isDuplicateDomainSection && PREFIX_PARENT_REDIRECTS[rawSectionHref]
    ? sectionHref
    : domainRoute[domain];

  return (
    <nav className={cn('flex items-center text-sm text-gray-500 mb-4 flex-wrap gap-y-1', location.pathname === '/ops/dispatch' && 'max-sm:hidden')} aria-label="Breadcrumb">
      {/* Home link */}
      <Link to="/" className="hover:text-blue-600 flex items-center transition-colors">
        <Home className="w-4 h-4" />
      </Link>

      {/* Domain indicator */}
      <ChevronRight className="w-4 h-4 mx-1.5 text-gray-400" />
      <Link
        to={domainHref}
        aria-current={isDuplicateDomainSection && pathnames.length === 1 ? 'page' : undefined}
        className={cn(
          'flex items-center gap-1.5 transition-colors',
          isDuplicateDomainSection && pathnames.length === 1
            ? 'font-medium text-gray-900 hover:text-blue-600'
            : 'text-gray-600 hover:text-blue-600',
        )}
      >
        <DomainIcon className="w-3.5 h-3.5" />
        <span className="font-medium">{domainNames[domain]}</span>
      </Link>

      {/* Section crumb (omitted when identical to the domain crumb) */}
      {!isDuplicateDomainSection && (
        <>
          <ChevronRight className="w-4 h-4 mx-1.5 text-gray-400" />
          <Link
            to={sectionHref}
            aria-current={pathnames.length === 1 ? 'page' : undefined}
            className="font-medium text-gray-700 hover:text-blue-600 transition-colors"
          >
            {sectionLabel}
          </Link>
        </>
      )}

      {/* Path segments */}
      {pathnames.map((value, index) => {
        const rawTo = `/${pathnames.slice(0, index + 1).join('/')}`;
        const to = resolveParentHref(rawTo);
        const isLast = index === pathnames.length - 1;
        const overrideLabel = overrides[rawTo] || overrides[to];

        // Check if this is a UUID (entity ID)
        const isUuidLike =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

        // Determine parent segment for context
        const parentSegment = index > 0 ? pathnames[index - 1] : null;

        // Get the display name
        let name: string;
        let EntityIcon: typeof User | null = null;

        if (overrideLabel) {
          name = overrideLabel;
        } else if (entityContext && value === entityContext.id) {
          name = entityContext.label;
          EntityIcon = entityIcons[entityContext.type];
        } else if (isUuidLike && isLast && entityContext) {
          // Use entity context for the last UUID segment
          name = entityContext.label;
          EntityIcon = entityIcons[entityContext.type];
        } else if (isUuidLike) {
          // Fallback for UUID segments based on parent
          const entityTypeMap: Record<string, string> = {
            'customers': 'Customer',
            'companies': 'Company',
            'travelers': 'Traveler',
            'invoices': 'Invoice',
            'trips': 'Trip',
            'partners': 'Partner',
            'suppliers': 'Supplier',
            'bookings': 'Booking',
          };
          name = parentSegment ? (entityTypeMap[parentSegment] || 'Details') : 'Details';
        } else {
          name = routeNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
        }

        // Skip segment(s) already represented by the domain or section crumb.
        if (index === 0 || (pathnames[0] === 'crm' && index === 1)) {
          return null;
        }

        return (
          <Fragment key={rawTo}>
            <ChevronRight className="w-4 h-4 mx-1.5 text-gray-400" />
            <Link
              to={to}
              aria-current={isLast ? 'page' : undefined}
              className={`transition-colors flex items-center gap-1.5 ${
                isLast
                  ? 'font-medium text-gray-900 hover:text-blue-600'
                  : 'hover:text-blue-600'
              }`}
            >
              {EntityIcon && <EntityIcon className="w-3.5 h-3.5 text-blue-600" />}
              {name}
              {entityContext?.subtitle && isUuidLike && (
                <span className="text-gray-500 font-normal">({entityContext.subtitle})</span>
              )}
            </Link>
          </Fragment>
        );
      })}
    </nav>
  );
}
